import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loginWithGoogle } from "store/auth/thunks";

const AuthEndpoint = () => {
const [searchParams] = useSearchParams();
const navigate = useNavigate();
const dispatch = useDispatch();
const [error, setError] = useState(null);
const [status, setStatus] = useState("Processing...");

// ============================================================
// Helper: Wait for GTM to be ready before pushing events
// ============================================================
const pushToDataLayer = (eventData) => {
return new Promise((resolve) => {
// If dataLayer already exists, push immediately
if (window.dataLayer) {
window.dataLayer.push(eventData);
console.log("[Auth Endpoint] ✅ Event pushed to dataLayer:", eventData);
resolve();
return;
}

// Otherwise, wait for GTM to load
console.log("[Auth Endpoint] ⏳ Waiting for GTM to load...");

// Check if dataLayer exists every 100ms, up to 5 seconds
let attempts = 0;
const maxAttempts = 50; // 5 seconds

const checkDataLayer = setInterval(() => {
attempts++;

if (window.dataLayer) {
clearInterval(checkDataLayer);
window.dataLayer.push(eventData);
console.log(
"[Auth Endpoint] ✅ Event pushed after waiting:",
eventData,
);
resolve();
return;
}

if (attempts >= maxAttempts) {
clearInterval(checkDataLayer);
// Fallback: create dataLayer if it doesn't exist
window.dataLayer = window.dataLayer || [];
window.dataLayer.push(eventData);
console.log(
"[Auth Endpoint] ⚠️ GTM timeout - event pushed to fallback dataLayer:",
eventData,
);
resolve();
}
}, 100);
});
};

// ============================================================
// Helper: Get OAuth code from sessionStorage (cleaned by HTML)
// ============================================================
const getOAuthCode = () => {
// First try sessionStorage (cleaned by HTML before GTM)
const code = sessionStorage.getItem("oauth_temp_code");
if (code) {
console.log(
"[Auth Endpoint] ✅ OAuth code retrieved from sessionStorage",
);
return code;
}
// Fallback to URL params (if HTML cleanup didn't run)
return searchParams.get("code");
};

const getOAuthState = () => {
const state = sessionStorage.getItem("oauth_temp_state");
if (state) return state;
return searchParams.get("state");
};

// Helper: Restore UTMs from sessionStorage to URL
const restoreUTMsToURL = () => {
try {
const utmKeys = [
"utm_source",
"utm_medium",
"utm_campaign",
"utm_content",
"utm_term",
"utm_id",
"gclid",
];
const url = new URL(window.location.href);
let urlChanged = false;

utmKeys.forEach((key) => {
const value = sessionStorage.getItem(key);
if (value && !url.searchParams.get(key)) {
url.searchParams.set(key, value);
urlChanged = true;
console.log(`[Auth Endpoint] Restored ${key}=${value} to URL`);
}
});

const storedUtms = sessionStorage.getItem("utm_params");
if (storedUtms) {
try {
const utms = JSON.parse(storedUtms);
Object.keys(utms).forEach((key) => {
if (utms[key] && !url.searchParams.get(key)) {
url.searchParams.set(key, utms[key]);
urlChanged = true;
}
});
} catch (e) {
console.error("[Auth Endpoint] Failed to parse stored UTMs:", e);
}
}

if (urlChanged) {
window.history.replaceState({}, "", url.toString());
console.log("[Auth Endpoint] ✅ UTMs restored to URL:", url.toString());
return true;
}

return false;
} catch (e) {
console.error("[Auth Endpoint] Failed to restore UTMs:", e);
return false;
}
};

// Helper: Get current URL with UTMs preserved
const getCurrentUrlWithUTMs = () => {
const url = new URL(window.location.href);
const utmKeys = [
"utm_source",
"utm_medium",
"utm_campaign",
"utm_content",
"utm_term",
"utm_id",
"gclid",
];
utmKeys.forEach((key) => {
const value = sessionStorage.getItem(key);
if (value) {
url.searchParams.set(key, value);
}
});
return url.toString();
};

// ============================================================
// Helper: Get the return URL (where to redirect after login)
// ============================================================
const getReturnUrl = () => {
// First check if there's a stored return URL in sessionStorage
const storedReturnUrl = sessionStorage.getItem("oauth_return_url");
if (storedReturnUrl) {
console.log("[Auth Endpoint] Found stored return URL:", storedReturnUrl);
// DO NOT remove it here - we need it for the redirect
return storedReturnUrl;
}

// Check if there's a 'state' parameter with return info
const stateParam = getOAuthState();
if (stateParam) {
try {
const parsedState = JSON.parse(decodeURIComponent(stateParam));
if (parsedState.returnUrl) {
console.log(
"[Auth Endpoint] Found return URL in state:",
parsedState.returnUrl,
);
return parsedState.returnUrl;
}
} catch (e) {
// State is not JSON, ignore
}
}

// Check if there's a 'return_to' parameter
const returnTo = searchParams.get("return_to");
if (returnTo) {
console.log("[Auth Endpoint] Found return_to parameter:", returnTo);
return returnTo;
}

// Default: dashboard
console.log("[Auth Endpoint] No return URL found, defaulting to dashboard");
return "/dashboard";
};

// ============================================================
// Helper: Check if this is a payment-result flow
// ============================================================
const isPaymentResultFlow = () => {
const returnUrl = getReturnUrl();
return returnUrl.includes("/payment-result");
};

// ============================================================
// 🔥 FIX: navigate() instead of window.location.href
// window.location.href triggers a full document unload, which can
// abort any in-flight network request (e.g. the GTM/GA4 beacon for
// google_login_success) that hasn't finished leaving the browser yet.
// navigate() is a client-side route change - no document unload -
// so it can't cancel anything that's already been dispatched.
// ============================================================
const goTo = (urlLike, { replace = false } = {}) => {
try {
const url =
urlLike instanceof URL
? urlLike
: new URL(urlLike, window.location.origin);

if (url.origin === window.location.origin) {
navigate(url.pathname + url.search + url.hash, { replace });
} else {
// Cross-origin target - a client-side route change can't handle
// this, so a full navigation is genuinely required here.
window.location.href = url.toString();
}
} catch (e) {
// Fallback for plain relative paths that fail URL parsing edge cases
console.error(
"[Auth Endpoint] goTo() failed to parse URL, falling back:",
e,
);
navigate(urlLike, { replace });
}
};

// ============================================================
// Helper: Redirect to return URL with UTMs
// ============================================================
const redirectToReturnUrl = (returnUrl) => {
console.log("[Auth Endpoint] Redirecting to:", returnUrl);

// Clear the stored return URL after redirect
sessionStorage.removeItem("oauth_return_url");

restoreUTMsToURL();

// Add UTMs to the return URL
try {
const url = new URL(returnUrl, window.location.origin);
const utmKeys = [
"utm_source",
"utm_medium",
"utm_campaign",
"utm_content",
"utm_term",
"utm_id",
"gclid",
];
utmKeys.forEach((key) => {
const value = sessionStorage.getItem(key);
if (value) {
url.searchParams.set(key, value);
}
});
setTimeout(() => {
goTo(url);
}, 500);
} catch (e) {
// If URL parsing fails, just navigate
setTimeout(() => {
navigate(returnUrl);
}, 500);
}
};

useEffect(() => {
const handleCallback = async () => {
// ============================================================
// 🔥 Get OAuth code from sessionStorage (cleaned by HTML)
// ============================================================
const code = getOAuthCode();
const stateParam = getOAuthState();
const errorParam = searchParams.get("error");

// Get the return URL first
const returnUrl = getReturnUrl();
const isPaymentResult = isPaymentResultFlow();

console.log("[Auth Endpoint] Callback received:", {
hasCode: !!code,
hasState: !!stateParam,
error: errorParam,
returnUrl,
isPaymentResult,
codeSource: code
? sessionStorage.getItem("oauth_temp_code")
? "sessionStorage"
: "URL"
: "none",
});

restoreUTMsToURL();

// Clear temp storage after use
sessionStorage.removeItem("oauth_temp_code");
sessionStorage.removeItem("oauth_temp_state");
sessionStorage.removeItem("oauth_temp_scope");

// ============================================================
// CRITICAL FIX: If this is a payment-result flow, handle it
// ============================================================
if (isPaymentResult) {
console.log("[Auth Endpoint] ✅ Payment-result flow detected");

if (errorParam) {
console.log(
"[Auth Endpoint] Error on payment-result flow:",
errorParam,
);
setTimeout(() => {
redirectToReturnUrl(returnUrl);
}, 1000);
return;
}

if (!code) {
console.log(
"[Auth Endpoint] No code on payment-result flow, going back",
);
setTimeout(() => {
redirectToReturnUrl(returnUrl);
}, 500);
return;
}

try {
const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const redirectUri = `${window.location.origin}/auth/auth-endpoint`;

setStatus("Completing authentication...");

const result = await dispatch(
loginWithGoogle({
code: code,
clientId: clientId,
redirectUri: redirectUri,
targetUid: null,
forBookingForm: false,
spaceId: null,
}),
).unwrap();

console.log("[Auth Endpoint] Payment-result login result:", result);

if (result?.data?.token) {
localStorage.setItem("accessToken", result.data.token);
console.log("[Auth Endpoint] Token stored for payment-result");
await pushToDataLayer({
event: "google_login_success",
login_method: "google",
account_status: "existing",
});
}

setTimeout(() => {
redirectToReturnUrl(returnUrl);
}, 500);
return;
} catch (err) {
console.error("[Auth Endpoint] Payment-result login error:", err);

if (err?.needsRegistration && err?.userData) {
console.log("[Auth Endpoint] New user on payment-result flow");
const googleId =
err.userData.google || err.userData.google_id || err.userData.sub;
sessionStorage.setItem(
"google_registration_data",
JSON.stringify({
userData: {
email: err.userData.email,
first_name: err.userData.first_name,
last_name: err.userData.last_name,
google_id: googleId,
google: googleId,
provider: "google",
},
provider: "google",
timestamp: Date.now(),
}),
);
const urlWithFlag = new URL(returnUrl, window.location.origin);
urlWithFlag.searchParams.set("registration_needed", "google");
setTimeout(() => {
goTo(urlWithFlag);
}, 500);
return;
}

setTimeout(() => {
redirectToReturnUrl(returnUrl);
}, 1000);
return;
}
}

// ============================================================
// NORMAL FLOW (not payment-result)
// ============================================================

if (errorParam) {
setError(`Authentication failed: ${errorParam.replace(/_/g, " ")}`);
setTimeout(() => {
restoreUTMsToURL();
navigate("/login");
}, 3000);
return;
}

const savedState = sessionStorage.getItem("google_oauth_state");
const oauthIntent = sessionStorage.getItem("google_oauth_intent");
const bookingContext = sessionStorage.getItem("booking_form_context");

console.log("[Auth Endpoint] Context:", {
oauthIntent,
hasBookingContext: !!bookingContext,
hasPendingBooking: !!sessionStorage.getItem("pending_booking_data"),
});

if (!savedState) {
setError("Security validation failed");
setTimeout(() => {
restoreUTMsToURL();
navigate("/login");
}, 3000);
return;
}

sessionStorage.removeItem("google_oauth_state");

if (!code) {
setError("No authorization code received");
setTimeout(() => {
restoreUTMsToURL();
navigate("/login");
}, 3000);
return;
}

setStatus("Getting your information...");

try {
const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const redirectUri = `${window.location.origin}/auth/auth-endpoint`;

const result = await dispatch(
loginWithGoogle({
code: code,
clientId: clientId,
redirectUri: redirectUri,
targetUid: null,
forBookingForm: oauthIntent === "booking_login" || !!bookingContext,
spaceId: null,
}),
).unwrap();

console.log("[Auth Endpoint] Result:", result);

if (result?.data?.token) {
  setStatus("Login successful! Redirecting...");

  if (result.data.token) {
    localStorage.setItem("accessToken", result.data.token);
    localStorage.setItem("token", result.data.token);
    console.log("[Auth Endpoint] Token stored in localStorage");
  }

  await pushToDataLayer({
    event: "google_login_success",
    login_method: "google",
    account_status: "existing",
  });

  const pendingBookingData = sessionStorage.getItem("pending_booking_data");
  const isBookingFlow =
    oauthIntent === "booking_login" || !!bookingContext;

  if (pendingBookingData && isBookingFlow) {
    // ============================================================
    // BOOKING FLOW — unchanged
    // ============================================================
    console.log("[Auth Endpoint] 📋 Pending booking data found");

    let spaceId = null;
    try {
      const data = JSON.parse(pendingBookingData);
      spaceId = data.spaceId;
      console.log("[Auth Endpoint] 📍 Space ID from pending data:", spaceId);
    } catch (e) {
      console.error(
        "[Auth Endpoint] Failed to parse pending booking data:",
        e,
      );
    }

    if (spaceId) {
      sessionStorage.setItem("has_pending_booking_after_oauth", "true");
      sessionStorage.setItem("just_logged_in_for_booking", "true");
      sessionStorage.setItem("booking_space_id", String(spaceId));
      console.log("[Auth Endpoint] ✅ Set booking flags for existing user");

      restoreUTMsToURL();

      setTimeout(() => {
        console.log(
          "[Auth Endpoint] 🔄 Redirecting to space page with auto-continue",
        );
        const redirectUrl = new URL(
          `/space/${spaceId}`,
          window.location.origin,
        );
        const utmKeys = [
          "utm_source",
          "utm_medium",
          "utm_campaign",
          "utm_content",
          "utm_term",
          "utm_id",
          "gclid",
        ];
        utmKeys.forEach((key) => {
          const value = sessionStorage.getItem(key);
          if (value) redirectUrl.searchParams.set(key, value);
        });
        goTo(redirectUrl, { replace: true });
      }, 500);
    } else {
      console.warn(
        "[Auth Endpoint] No spaceId found in pending data, redirecting to dashboard",
      );
      restoreUTMsToURL();
      setTimeout(() => navigate("/dashboard", { replace: true }), 1000);
    }
  } else {
    // ============================================================
    // NON-BOOKING FLOW
    // First: opt-in explicit return URL (e.g. /basket)
    // ============================================================
    const explicitReturnUrl = sessionStorage.getItem(
      "post_login_return_url",
    );

    if (explicitReturnUrl && explicitReturnUrl.startsWith("/")) {
      console.log(
        "[Auth Endpoint] Explicit post-login return URL:",
        explicitReturnUrl,
      );

      sessionStorage.removeItem("post_login_return_url");
      restoreUTMsToURL();

      setTimeout(() => {
        const returnUrl = new URL(
          explicitReturnUrl,
          window.location.origin,
        );

        const utmKeys = [
          "utm_source",
          "utm_medium",
          "utm_campaign",
          "utm_content",
          "utm_term",
          "utm_id",
          "gclid",
        ];
        utmKeys.forEach((key) => {
          const value = sessionStorage.getItem(key);
          if (value) returnUrl.searchParams.set(key, value);
        });

        goTo(returnUrl, { replace: true });
      }, 800);
    } else {
      // Fallback: dashboard
      console.log(
        "[Auth Endpoint] Regular login, redirecting to dashboard",
      );
      restoreUTMsToURL();

      const dashboardUrl = new URL("/dashboard", window.location.origin);
      const utmKeys = [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_content",
        "utm_term",
        "utm_id",
        "gclid",
      ];
      utmKeys.forEach((key) => {
        const value = sessionStorage.getItem(key);
        if (value) dashboardUrl.searchParams.set(key, value);
      });

      setTimeout(() => {
        goTo(dashboardUrl, { replace: true });
      }, 1000);
    }
  }
}
} catch (err) {
console.error("[Auth Endpoint] Error:", err);

// ============================================================
// HANDLE NEW USER REGISTRATION
// ============================================================
if (err?.needsRegistration && err?.userData) {
console.log(
"[Auth Endpoint] New user - storing data for registration",
);

const googleId =
err.userData.google || err.userData.google_id || err.userData.sub;

sessionStorage.setItem(
"google_registration_data",
JSON.stringify({
userData: {
email: err.userData.email,
first_name: err.userData.first_name,
last_name: err.userData.last_name,
google_id: googleId,
google: googleId,
provider: "google",
},
provider: "google",
timestamp: Date.now(),
}),
);

// ============================================================
// DETERMINE IF THIS IS A BOOKING FLOW
// ============================================================
const isBookingFlow =
oauthIntent === "booking_login" || !!bookingContext;

// ============================================================
// FIX: Get the correct user type from sessionStorage
// ============================================================
const userType =
sessionStorage.getItem("google_signup_type") || "seeker";
console.log("[Auth Endpoint] User type for registration:", userType);
console.log("[Auth Endpoint] Is booking flow:", isBookingFlow);

// ============================================================
// HANDLE BOOKING FLOW - NEW USER
// ============================================================
if (isBookingFlow) {
setStatus("New user! Redirecting to complete your booking...");

await pushToDataLayer({
event: "google_login_success",
login_method: "google",
account_status: "new",
});

// ============================================================
// ADDED: Set just_registered_for_booking flag for SpacePage
// ============================================================
sessionStorage.setItem("has_pending_booking_after_oauth", "true");
sessionStorage.setItem("just_logged_in_for_booking", "true");
sessionStorage.setItem("just_registered_for_booking", "true");

console.log("[AuthEndpoint] ✅ Set booking flags for new user");

const pendingData = sessionStorage.getItem("pending_booking_data");
if (pendingData) {
try {
const data = JSON.parse(pendingData);
if (data.spaceId) {
sessionStorage.setItem(
"booking_space_id",
String(data.spaceId),
);
console.log(
"[Auth Endpoint] ✅ Set booking_space_id =",
data.spaceId,
);
}
} catch (e) {
console.error(
"[Auth Endpoint] Failed to parse pending data:",
e,
);
}
}

restoreUTMsToURL();

setTimeout(() => {
const registerUrl = new URL(
`/register/${userType}`,
window.location.origin,
);
registerUrl.searchParams.set("booking_flow", "true");
console.log(
"[Auth Endpoint] 🔄 Redirecting to registration with booking_flow=true:",
registerUrl.toString(),
);

const utmKeys = [
"utm_source",
"utm_medium",
"utm_campaign",
"utm_content",
"utm_term",
"utm_id",
"gclid",
];
utmKeys.forEach((key) => {
const value = sessionStorage.getItem(key);
if (value) {
registerUrl.searchParams.set(key, value);
}
});
goTo(registerUrl);
}, 1500);

// ============================================================
// HANDLE SIGNUP FLOW - NEW USER (non-booking)
// ============================================================
} else if (oauthIntent === "signup") {
setStatus("New user! Redirecting to registration...");
await pushToDataLayer({
event: "google_login_success",
login_method: "google",
account_status: "new",
});
restoreUTMsToURL();
setTimeout(() => {
const registerUrl = new URL(
`/register/${userType}`,
window.location.origin,
);
console.log(
"[Auth Endpoint] 🔄 Redirecting to registration (signup flow):",
registerUrl.toString(),
);

const utmKeys = [
"utm_source",
"utm_medium",
"utm_campaign",
"utm_content",
"utm_term",
"utm_id",
"gclid",
];
utmKeys.forEach((key) => {
const value = sessionStorage.getItem(key);
if (value) {
registerUrl.searchParams.set(key, value);
}
});
goTo(registerUrl);
}, 1500);

// ============================================================
// DEFAULT FALLBACK
// ============================================================
} else {
setStatus("New user! Redirecting to registration...");
await pushToDataLayer({
event: "google_login_success",
login_method: "google",
account_status: "new",
});
restoreUTMsToURL();
setTimeout(() => {
const registerUrl = new URL(
`/register/${userType}`,
window.location.origin,
);
console.log(
"[Auth Endpoint] �� Redirecting to registration (default fallback):",
registerUrl.toString(),
);

const utmKeys = [
"utm_source",
"utm_medium",
"utm_campaign",
"utm_content",
"utm_term",
"utm_id",
"gclid",
];
utmKeys.forEach((key) => {
const value = sessionStorage.getItem(key);
if (value) {
registerUrl.searchParams.set(key, value);
}
});
goTo(registerUrl);
}, 1500);
}

// ============================================================
// HANDLE OTHER ERRORS
// ============================================================
} else {
const errorMessage =
typeof err === "string"
? err
: err?.message || err?.[0]?.message || "Authentication failed";
setError(errorMessage);
restoreUTMsToURL();
setTimeout(() => {
const loginUrl = new URL("/login", window.location.origin);
const utmKeys = [
"utm_source",
"utm_medium",
"utm_campaign",
"utm_content",
"utm_term",
"utm_id",
"gclid",
];
utmKeys.forEach((key) => {
const value = sessionStorage.getItem(key);
if (value) {
loginUrl.searchParams.set(key, value);
}
});
goTo(loginUrl);
}, 3000);
}
}
};

handleCallback();
}, [searchParams, navigate, dispatch]);

const containerStyle = {
display: "flex",
justifyContent: "center",
alignItems: "center",
minHeight: "100vh",
backgroundColor: "#f5f5f5",
};

const cardStyle = {
backgroundColor: "white",
borderRadius: "8px",
padding: "40px",
boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
textAlign: "center",
maxWidth: "400px",
width: "90%",
};

const loaderStyle = {
display: "inline-block",
width: "40px",
height: "40px",
border: "4px solid #f3f3f3",
borderTop: "4px solid #3498db",
borderRadius: "50%",
animation: "spin 1s linear infinite",
marginTop: "20px",
};

const errorStyle = {
color: "#e74c3c",
marginBottom: "20px",
};

if (error) {
return (
<div style={containerStyle}>
<div style={cardStyle}>
<h2>Authentication Error</h2>
<p style={errorStyle}>{error}</p>
<p>Redirecting to login page...</p>
</div>
</div>
);
}

return (
<div style={containerStyle}>
<div style={cardStyle}>
<h2>Google Authentication</h2>
<p>{status}</p>
<div style={loaderStyle}></div>
<style>
{`
@keyframes spin {
0% { transform: rotate(0deg); }
100% { transform: rotate(360deg); }
}
`}
</style>
</div>
</div>
);
};

export default AuthEndpoint;

